import { z } from "zod";
import { residencyViewSchema } from "./models.ts";
import { entityIdSchema, userIdSchema } from "./tenancy.ts";

/**
 * Projects (openspec change `projects-and-knowledge`): workspaces owned by a user (personal)
 * or an entity, with members, persisted conversations and knowledge. Every project belongs to
 * exactly one tenant, which always comes from the authenticated identity, never from input.
 */
export const projectIdSchema = z.string().brand<"ProjectId">();
export const conversationIdSchema = z.string().brand<"ConversationId">();
export const messageIdSchema = z.string().brand<"MessageId">();

export type ProjectId = z.infer<typeof projectIdSchema>;
export type ConversationId = z.infer<typeof conversationIdSchema>;
export type MessageId = z.infer<typeof messageIdSchema>;

/**
 * - `private`: project members only (always, for personal projects);
 * - `entity`: also members of the owning entity, as `reader`;
 * - `entity_tree`: also members of the owning entity and its descendants, as `reader`.
 */
export const projectVisibilitySchema = z.enum(["private", "entity", "entity_tree"]);
export type ProjectVisibility = z.infer<typeof projectVisibilitySchema>;

/**
 * The five fixed project roles (openspec change `project-roles`), highest first: `owner` (one per
 * project, accountable: deletes, transfers), `manager` (members, grants, settings, declassifies),
 * `contributor` (uploads, publishes to the wiki), `reader` (converses, reads, exports) and
 * `guest` (only what is explicitly shared with them). What each may do is the core permission
 * matrix (`PROJECT_PERMISSIONS`); clients read `ProjectView.capabilities`, never compare roles.
 */
export const projectRoleSchema = z.enum(["owner", "manager", "contributor", "reader", "guest"]);
export type ProjectRole = z.infer<typeof projectRoleSchema>;

/** The roles a member, group or attribute-rule grant may be given: `owner` only by a transfer. */
export const assignableProjectRoleSchema = projectRoleSchema.exclude(["owner"]);
export type AssignableProjectRole = z.infer<typeof assignableProjectRoleSchema>;

/** What a project role may do; the core matrix maps each to the roles that hold it. */
export const projectCapabilitySchema = z.enum([
  "file.upload",
  "file.manage_own",
  "file.manage_any",
  "file.restricted_count",
  "file.save_answer",
  "conversation.share",
  "wiki.view_drafts",
  "wiki.draft",
  "wiki.publish",
  "wiki.resolve_review",
  "wiki.delete_page",
  "wiki.curate",
  "wiki.export",
  "wiki.comment",
  "folder.manage",
  "task.view",
  "task.create",
  "task.manage",
  "declassify",
  "memory.view",
  "memory.propose",
  "memory.manage",
  "project.view_members",
  "project.view_activity",
  "members.manage",
  "grants.manage",
  "project.edit",
  "owner.transfer",
  "project.request_promotion",
  "project.delete",
]);
export type ProjectCapability = z.infer<typeof projectCapabilitySchema>;

export const projectOwnerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), userId: userIdSchema }),
  z.object({ kind: z.literal("entity"), entityId: entityIdSchema }),
]);
export type ProjectOwner = z.infer<typeof projectOwnerSchema>;

export const projectSchema = z.object({
  id: projectIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  owner: projectOwnerSchema,
  visibility: projectVisibilitySchema,
  /** Project-level setting overrides, applied only where the entity does not lock the key. */
  settings: z.record(z.string(), z.unknown()),
  createdBy: userIdSchema,
  createdAt: z.iso.datetime(),
  /** Set while the project is read-only. */
  frozenAt: z.iso.datetime().nullable(),
});
export type Project = z.infer<typeof projectSchema>;

export const projectMemberSchema = z.object({
  userId: userIdSchema,
  role: projectRoleSchema,
  /** Added through audited break-glass (always `reader`, with a reason). */
  viaBreakGlass: z.boolean(),
  breakGlassReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

/** A member as listed to project readers: with the name to show (display name, else email). */
export const projectMemberViewSchema = projectMemberSchema.extend({
  displayName: z.string().nullable(),
  /** Only for callers who may manage members (spec `projects` "Member e-mail addresses"). */
  email: z.string().nullable(),
});
export type ProjectMemberView = z.infer<typeof projectMemberViewSchema>;

/** Shortest and longest people search query, and the most people one search returns. */
export const PEOPLE_SEARCH_MIN = 2;
export const PEOPLE_SEARCH_MAX = 100;
export const PEOPLE_SEARCH_LIMIT = 8;

/** A prefix of a display name or e-mail address (spec `projects` "People search bounds"). */
export const peopleSearchQuerySchema = z.object({
  q: z.string().trim().min(PEOPLE_SEARCH_MIN).max(PEOPLE_SEARCH_MAX),
});

/** One person a project manager may add: no more than the members dialog shows. */
export const projectPersonSchema = z.object({
  userId: userIdSchema,
  displayName: z.string().nullable(),
  email: z.string(),
  /** Already a direct member of the project. */
  member: z.boolean(),
});
export type ProjectPerson = z.infer<typeof projectPersonSchema>;

export const promotionStatusSchema = z.enum(["pending", "accepted", "rejected"]);
export type PromotionStatus = z.infer<typeof promotionStatusSchema>;

/** One-way request to move a personal project to an entity; an entity admin decides. */
export const projectPromotionSchema = z.object({
  id: z.string(),
  projectId: projectIdSchema,
  targetEntityId: entityIdSchema,
  requestedBy: userIdSchema,
  status: promotionStatusSchema,
  /** The decision's reason (required to reject). */
  reason: z.string().nullable(),
  decidedBy: userIdSchema.nullable(),
  createdAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
});
export type ProjectPromotion = z.infer<typeof projectPromotionSchema>;

/**
 * The OAuth client a conversation was created through (an MCP `ask_project`, spec
 * `conversations`): its client ID and that ID's host, or null for a client ID that is not an
 * `https` URL (ESI's own command-line tool, `esi-cli`).
 */
export const conversationClientSchema = z.object({
  id: z.string(),
  host: z.string().nullable(),
});
export type ConversationClient = z.infer<typeof conversationClientSchema>;

/** The client view of a stored client ID; null when the conversation was created in ESI. */
export function conversationClientOf(clientId: string | null): ConversationClient | null {
  if (clientId === null) {
    return null;
  }
  const url = URL.canParse(clientId) ? new URL(clientId) : null;
  return { id: clientId, host: url?.protocol === "https:" ? url.hostname : null };
}

/**
 * The task a conversation works on (tasks-conversations): its ID for every viewer, its title
 * only for one who can read the task now. Branded like `taskIdSchema` (`tasks.ts`, which imports
 * this module).
 */
export const conversationTaskSchema = z.object({
  id: z.uuid().brand<"TaskId">(),
  title: z.string().nullable(),
});
export type ConversationTask = z.infer<typeof conversationTaskSchema>;

export const conversationSchema = z.object({
  id: conversationIdSchema,
  /** Null for a standalone conversation (outside any project, spec `conversations`). */
  projectId: projectIdSchema.nullable(),
  createdBy: userIdSchema,
  /** Null until generated, and for viewers who fail the conversation's label. */
  title: z.string().nullable(),
  /** Set when its creator archived it (null for everyone else: archiving is per creator). */
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  /** Set when it was created through an MCP client; optional for one release (canary). */
  client: conversationClientSchema.nullable().optional(),
  /**
   * The task it was started from; absent or null for any other conversation and once the task
   * is deleted. Optional for one release (canary); never on list items.
   */
  task: conversationTaskSchema.nullable().optional(),
});
export type Conversation = z.infer<typeof conversationSchema>;

export const messageRoleSchema = z.enum(["user", "assistant"]);
export type MessageRole = z.infer<typeof messageRoleSchema>;

const nameSchema = z.string().trim().min(1).max(200);

// Request bodies. Zod objects strip unknown keys: a forged `tenantId` never reaches a service.
export const createProjectRequestSchema = z.object({
  name: nameSchema,
  description: z.string().trim().max(2000).optional(),
  /** Omitted: a personal project owned by the caller. */
  ownerEntityId: z.uuid().optional(),
  visibility: projectVisibilitySchema.optional(),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

/** A project as seen by one user, with the role they act in it with and what it allows. */
export const projectViewSchema = projectSchema.extend({
  role: projectRoleSchema,
  capabilities: z.array(projectCapabilitySchema),
});
export type ProjectView = z.infer<typeof projectViewSchema>;

/**
 * One project as opened by a reader: also the owning entity's name (null when personal), and its
 * resolved `models.residency` (`global`: its model calls may be processed outside the EU).
 */
export const projectDetailSchema = projectViewSchema.extend({
  ownerName: z.string().nullable(),
  residency: residencyViewSchema,
  /**
   * The agent may run code here (`tools.codeExecution` on, a sandbox deployed): the residency
   * note then says where code runs (agent-turn, data-residency). Optional for one release.
   */
  codeExecution: z.boolean().optional(),
});
export type ProjectDetail = z.infer<typeof projectDetailSchema>;

export const updateProjectRequestSchema = z
  .object({
    name: nameSchema.optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    visibility: projectVisibilitySchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), "Nothing to update");
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

/** Adds a user of the caller's tenant, by ID or by email. */
export const addProjectMemberRequestSchema = z
  .object({
    userId: z.uuid().optional(),
    email: z.email().optional(),
    role: assignableProjectRoleSchema,
  })
  .refine((v) => (v.userId === undefined) !== (v.email === undefined), {
    message: "Give exactly one of userId or email",
  });
export type AddProjectMemberRequest = z.infer<typeof addProjectMemberRequestSchema>;

/**
 * Any of the five roles parses, so asking for `owner` is refused by the role ceiling (`403`:
 * only a transfer makes an owner) rather than as malformed; `admin` or `viewer` are `422`.
 */
export const setProjectMemberRoleRequestSchema = z.object({ role: projectRoleSchema });

/** Makes another direct member `owner`; the current owner becomes `manager`. */
export const transferProjectOwnershipRequestSchema = z.object({ userId: z.uuid() });
export type TransferProjectOwnershipRequest = z.infer<typeof transferProjectOwnershipRequestSchema>;

/** `value` is required: clearing an override is a DELETE, never a missing key. */
export const putProjectSettingRequestSchema = z
  .object({ value: z.unknown() })
  .refine((b) => b.value !== undefined, { message: "value is required", path: ["value"] });

export const requestPromotionRequestSchema = z.object({ entityId: z.uuid() });

/** Accepting needs no reason; rejecting does, and the requester sees it. */
export const acceptPromotionRequestSchema = z.object({
  reason: z.string().trim().max(2000).optional(),
});
export const rejectPromotionRequestSchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});

/** A pending promotion as shown to the entity admins who can decide it. */
export const pendingPromotionSchema = projectPromotionSchema.extend({ projectName: z.string() });
export type PendingPromotion = z.infer<typeof pendingPromotionSchema>;
